// The waiting room, shared by both ways into a table: the host arrives here
// from `OnlineCreateScreen`, and the join-by-code screen (plan T16) will land
// here too — nothing below reads the host's form, only `LobbyInfo`, and the
// host-only bits are gated by `canStartNow`.
//
// The screen never navigates to the table itself. `GAME_START` reaches
// `scenes/OnlineMenuHost.ts` whether the room filled by itself or the host
// pressed "Start now", and that is the one place the handoff happens.

import type { MenuScreen, MahjongMenuContext } from '../types.js';
import type { LobbyInfo } from '@bn-games/room-client';
import { ROOM_CODE_LENGTH } from '@bn-games/network-protocol';
import { isRulesetId } from '../../network/protocol.js';
import {
  createScreenShell,
  createMenuHeader,
  createCrestButton,
  createCopyButton,
  createInviteFriendButton,
  el,
} from '@bn-games/ui-shell';
import { isDiscordActivity } from '@bn-games/discord-activity';
import { t } from '../../i18n/I18n.js';
import { buildSeatRoster, canStartNow, isSeatedWaiting, type LobbySeatRow, type LobbyView } from './onlineLobbyModel.js';

/** Spaced out so the code can be read aloud a character at a time. */
function formatRoomCode(code: string | null): string {
  return code === null ? new Array(ROOM_CODE_LENGTH).fill('·').join(' ') : code.split('').join(' ');
}

function viewOf(ctx: MahjongMenuContext, info: LobbyInfo): LobbyView {
  return {
    humanSeats: info.seatCount ?? ctx.online.getForm().humanSeats,
    localSeat: info.localSeat,
    players: info.players,
    isHost: ctx.online.isHost(),
  };
}

function occupantLabel(row: LobbySeatRow): string {
  if (row.name !== null) {
    return row.kind === 'you' ? `${row.name} (${t('online.lobby.you')})` : row.name;
  }
  if (row.kind === 'you') return t('online.lobby.you');
  return row.kind === 'bot' ? t('online.lobby.bot') : t('online.lobby.open');
}

function seatLine(row: LobbySeatRow): HTMLElement {
  const seatLabel = t('online.lobby.seat', { n: row.seat + 1 });
  return el('div', { class: 'online-hint', text: `${seatLabel} — ${occupantLabel(row)}` });
}

function statusLine(ctx: MahjongMenuContext, info: LobbyInfo): HTMLElement {
  const node = el('div', { class: 'online-status' });
  if (isSeatedWaiting(viewOf(ctx, info), info.state)) {
    node.textContent = t('online.lobby.status.waiting');
    node.classList.add('waiting-dots');
    return node;
  }
  switch (info.state) {
    case 'error':
      node.textContent = info.errorMessage ?? t('online.lobby.status.error');
      return node;
    case 'ready':
      node.textContent = t('online.lobby.status.ready');
      return node;
    case 'connecting':
    case 'creating':
    case 'joining':
      node.textContent = t('online.lobby.status.connecting');
      node.classList.add('waiting-dots');
      return node;
    default:
      node.textContent = t('online.lobby.status.waiting');
      node.classList.add('waiting-dots');
      return node;
  }
}

export function createOnlineLobbyScreen(): MenuScreen {
  let frame: HTMLElement | null = null;
  let root: HTMLElement | null = null;
  let unsubRoom: (() => void) | null = null;

  function buildRoom(ctx: MahjongMenuContext, info: LobbyInfo): HTMLElement {
    const code = info.roomCode;
    const variant = info.variant;
    // Inside Discord the seat is won by launching the activity, not by typing a
    // code, so the code block would only invite a route that leads elsewhere.
    // The roster and the host's Start button are what the player came for.
    const showCode = !isDiscordActivity();
    const inviteBtn =
      code !== null && showCode ? createInviteFriendButton({ game: 'mahjong', roomCode: code }) : null;
    const center = el('div', { class: 'online-center' }, [
      showCode ? el('div', { class: 'online-label', text: t('online.lobby.codeLabel') }) : null,
      showCode ? el('div', { class: 'online-code', text: formatRoomCode(code) }) : null,
      code === null || !showCode
        ? null
        : el('div', { class: 'button-row' }, [
            createCopyButton({
              label: t('online.lobby.copyCode'),
              copiedLabel: t('online.lobby.copied'),
              getText: () => code,
            }),
          ]),
      inviteBtn ? el('div', { class: 'button-row' }, [inviteBtn]) : null,
      showCode ? el('div', { class: 'online-hint', text: t('online.lobby.share') }) : null,
      variant !== null && isRulesetId(variant)
        ? el('div', {
            class: 'online-hint',
            text: t('online.lobby.rulesetLabel', { ruleset: t(`settings.variants.${variant}`) }),
          })
        : null,
      info.verifiable
        ? el('div', { class: 'online-hint', text: t('online.lobby.verifiableOn') })
        : null,
    ]);
    for (const row of buildSeatRoster(viewOf(ctx, info))) center.appendChild(seatLine(row));
    center.appendChild(statusLine(ctx, info));
    return center;
  }

  function buildActions(ctx: MahjongMenuContext, info: LobbyInfo): HTMLElement | null {
    if (!canStartNow(viewOf(ctx, info), info.state)) return null;
    return el('div', {}, [
      el('div', { class: 'button-stack' }, [
        createCrestButton({
          label: t('online.lobby.startNow'),
          primary: true,
          onClick: () => ctx.online.startGame(),
        }),
      ]),
      el('div', { class: 'online-hint', text: t('online.lobby.startNowHint') }),
    ]);
  }

  function paint(ctx: MahjongMenuContext): void {
    if (root === null) return;
    while (root.firstChild) root.removeChild(root.firstChild);
    const shell = createScreenShell();
    frame = shell.frame;
    const body = shell.body;

    const info = ctx.online.getRoomInfo();
    body.appendChild(createMenuHeader({ title: t('online.lobby.title') }));

    const center = buildRoom(ctx, info);
    const actions = buildActions(ctx, info);
    if (actions !== null) center.appendChild(actions);
    body.appendChild(center);

    body.appendChild(
      el('div', { class: 'button-row back-button-row' }, [
        createCrestButton({
          label: t('online.lobby.leave'),
          small: true,
          onClick: () => {
            ctx.online.leaveRoom();
            ctx.navigate('play');
          },
        }),
      ]),
    );
    root.appendChild(frame);
  }

  return {
    mount(rootEl, ctx) {
      root = rootEl;
      unsubRoom = ctx.online.onRoomUpdate(() => paint(ctx));
      paint(ctx);
    },
    unmount() {
      unsubRoom?.();
      unsubRoom = null;
      frame?.remove();
      frame = null;
      root = null;
    },
  };
}
